import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import reportsService from '../services/reportsService';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { formatInStoreTimezone, toApiDateValue } from '../utils/dateUtils';

const formatCurrency = (value = 0) => `$${Number(value || 0).toFixed(2)}`;

const defaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return {
    startDate: toApiDateValue(start),
    endDate: toApiDateValue(end),
  };
};

const SalesReportPage = () => {
  const { user } = useAuth();
  const { activeStore } = useStore();
  const timezone = useStoreTimezone();
  const canViewReports = user?.permissions?.includes('reports:view');

  const [filters, setFilters] = useState(() => defaultFilters());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canViewReports || !activeStore) {
      setReportData(null);
      return;
    }
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await reportsService.getSalesReport({
          ...filters,
          timezone,
        });
        setReportData(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load sales report.');
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [filters, activeStore, canViewReports, timezone]);

  const summary = useMemo(
    () =>
      reportData?.summary || {
        totalSales: 0,
        totalGst: 0,
        totalDiscount: 0,
        transactionCount: 0,
        avgSaleValue: 0,
      },
    [reportData]
  );

  if (!canViewReports) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800">Sales Report</h1>
        <p className="mt-4 text-gray-600">You do not have permission to view sales reports.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Report</h1>
          <p className="text-gray-500">Track sales performance for {activeStore?.name || 'your store'}.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end bg-white p-4 rounded-xl shadow">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              From
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
              className="border rounded-md px-3 py-2 text-sm"
              max={filters.endDate}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              To
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
              className="border rounded-md px-3 py-2 text-sm"
              min={filters.startDate}
            />
          </div>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Total Sales</p>
          <p className="text-2xl font-semibold">{formatCurrency(summary.totalSales)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Transactions</p>
          <p className="text-2xl font-semibold">{summary.transactionCount}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">Avg. Sale</p>
          <p className="text-2xl font-semibold">{formatCurrency(summary.avgSaleValue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-xs uppercase text-gray-500">GST Collected</p>
          <p className="text-2xl font-semibold">{formatCurrency(summary.totalGst)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Daily Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-600">Transactions</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-600">Sales</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-600">GST</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-600">Discounts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && reportData?.rows?.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-gray-500">
                      No data for selected range.
                    </td>
                  </tr>
                )}
                {!loading &&
                  reportData?.rows?.map((row) => (
                    <tr key={row.date}>
                      <td className="px-4 py-2">
                        {formatInStoreTimezone(row.date, 'dd-MM-yyyy', timezone)}
                      </td>
                      <td className="px-4 py-2 text-right">{row.transactionCount}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.totalSales)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.gstAmount)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.discountAmount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Breakdown</h2>
          <div className="space-y-3">
            {reportData?.paymentBreakdown?.length ? (
              reportData.paymentBreakdown.map((method) => (
                <div
                  key={method.method}
                  className="flex items-center justify-between border-b pb-2 text-sm"
                >
                  <div>
                    <p className="font-semibold text-gray-800">{method.method}</p>
                    <p className="text-xs text-gray-500">{method.count} transactions</p>
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(method.total)}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No payment data.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReportPage;
